/**
 * Feb 2026 · /e/:code — Public enquiry customer portal.
 *
 * Landing page linked from the auto-reply email. The ENQ code itself
 * is the shared secret (no auth). Shows the enquiry status timeline,
 * lets the customer upload reference images and nudge for an update.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const STATUS_LABELS = {
  new: "Received",
  contacted: "We reached out",
  qualified: "Qualified",
  quoted: "Quote sent",
  won: "Confirmed",
  lost: "Closed",
};

const STATUS_ORDER = ["new", "contacted", "qualified", "quoted", "won"];

function fmt(dt) {
  if (!dt) return "";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

function absUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

export default function EnquiryPortal() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [nudgeText, setNudgeText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [refLabel, setRefLabel] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/enquiries/lookup/${encodeURIComponent(code)}`);
      setData(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Enquiry not found");
    }
  };
  useEffect(() => { if (code) load(); /* eslint-disable-next-line */ }, [code]);

  const stepIndex = useMemo(() => {
    if (!data) return -1;
    if (data.status === "lost") return -1;
    return STATUS_ORDER.indexOf(data.status);
  }, [data]);

  const nudge = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/enquiries/lookup/${encodeURIComponent(code)}/nudge`,
        { note: nudgeText.slice(0, 500) });
      setNudgeText("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Nudge failed — please try again shortly");
    } finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (refLabel) fd.append("label", refLabel);
      await axios.post(`${API}/public/enquiries/lookup/${encodeURIComponent(code)}/upload-reference`,
        fd, { headers: { "Content-Type": "multipart/form-data" }, params: refLabel ? { label: refLabel } : {} });
      setRefLabel("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed — please try a smaller image");
    } finally { setUploading(false); }
  };

  if (err && !data) {
    return (
      <main className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="enq-portal-error">
        <div className="max-w-md w-full border border-border bg-white p-8 text-center">
          <div className="text-xs uppercase tracking-widest text-rose-700 font-semibold mb-2">Not found</div>
          <h1 className="text-xl font-semibold mb-2" style={{ fontFamily: `"Cormorant Garamond", Georgia, serif` }}>
            We couldn't find that enquiry
          </h1>
          <p className="text-sm text-muted-foreground">
            The link may have expired or the code is wrong. Please check the email we sent you.
          </p>
        </div>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="text-sm text-muted-foreground">Loading your enquiry…</div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-neutral-50 p-4 sm:p-8" data-testid="enq-portal">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700/80 font-semibold mb-1">
            {data.workspace_name || "Studio"}
          </div>
          <h1 className="text-3xl sm:text-4xl font-medium leading-tight"
              style={{ fontFamily: `"Cormorant Garamond", Georgia, serif`, letterSpacing: "-0.02em" }}
              data-testid="enq-portal-heading">
            Hi {data.customer_name || "there"} — here's your enquiry
          </h1>
          <div className="text-xs text-muted-foreground mt-2 font-mono" data-testid="enq-portal-code">
            {data.code} · {data.type_name}
          </div>
        </div>

        {/* Progress pipeline */}
        <section className="border border-border bg-white p-5 mb-6" data-testid="enq-portal-progress">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
            Progress
          </div>
          {data.status === "lost" ? (
            <div className="text-sm text-rose-700 border border-rose-300 bg-rose-50 px-3 py-2">
              This enquiry was closed. If that's unexpected, please reach out to us.
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-1">
              {STATUS_ORDER.map((s, i) => {
                const done = i <= stepIndex;
                const current = i === stepIndex;
                return (
                  <div key={s} className="flex flex-col items-center gap-1.5"
                       data-testid={`enq-portal-step-${s}`}>
                    <div className={`w-full h-1.5 ${done ? "bg-amber-600" : "bg-neutral-200"}`} />
                    <div className={`text-[9px] sm:text-[10px] uppercase tracking-wider text-center leading-tight ${
                      current ? "font-bold text-amber-800" : done ? "text-foreground" : "text-muted-foreground"
                    }`}>
                      {STATUS_LABELS[s]}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Your original message */}
        {data.message && (
          <section className="border border-border bg-white p-5 mb-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Your message
            </div>
            <div className="text-sm whitespace-pre-wrap" data-testid="enq-portal-message">{data.message}</div>
          </section>
        )}

        {/* Reference images */}
        <section className="border border-border bg-white p-5 mb-6" data-testid="enq-portal-refs">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Reference images ({(data.references || []).length})
            </div>
          </div>
          {data.references && data.references.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {data.references.map((r, i) => (
                <a key={i} href={absUrl(r.url)} target="_blank" rel="noopener noreferrer"
                   className="block border border-border hover:border-amber-600 transition-colors group"
                   data-testid={`enq-portal-ref-${i}`}>
                  {r.kind === "image" || /\.(jpg|jpeg|png|webp|gif)$/i.test(r.url) ? (
                    <img src={absUrl(r.url)} alt={r.label || "reference"}
                         className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-muted/40 flex items-center justify-center text-[10px] text-muted-foreground p-2 text-center">
                      {r.label || "link"}
                    </div>
                  )}
                  <div className="p-1 text-[9px] text-muted-foreground truncate group-hover:text-foreground">
                    {r.label || "reference"}
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic mb-3">
              No references shared yet. Add a mood-board image to help us plan better.
            </div>
          )}
          <div className="border-t border-border pt-3 space-y-2">
            <input type="text" value={refLabel}
                    onChange={(e) => setRefLabel(e.target.value)}
                    placeholder="Optional label (e.g. Colour palette)"
                    className="w-full h-9 px-3 border border-border bg-background text-xs"
                    data-testid="enq-portal-ref-label" />
            <label className="block">
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => upload(e.target.files?.[0])}
                      className="hidden" data-testid="enq-portal-ref-file"
                      disabled={uploading} />
              <span className={`block text-center text-[11px] uppercase tracking-widest font-semibold py-2.5 border cursor-pointer transition-colors ${
                uploading
                  ? "border-neutral-300 text-muted-foreground bg-muted/20"
                  : "border-amber-600 text-amber-800 hover:bg-amber-50"
              }`}>
                {uploading ? "Uploading…" : "+ Upload reference image"}
              </span>
            </label>
            <div className="text-[10px] text-muted-foreground">JPG, PNG, WebP or GIF up to 8 MB.</div>
          </div>
        </section>

        {/* Nudge */}
        <section className="border border-border bg-white p-5 mb-6" data-testid="enq-portal-nudge-section">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Need an update?
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Send us a quick nudge and our team will get back to you.
          </p>
          <textarea rows={3} value={nudgeText}
                     onChange={(e) => setNudgeText(e.target.value.slice(0, 500))}
                     placeholder="Any update on my enquiry?"
                     className="w-full p-3 border border-border bg-background text-xs mb-2"
                     data-testid="enq-portal-nudge-text" />
          <button onClick={nudge} disabled={busy}
                   className="w-full sm:w-auto px-6 py-2.5 bg-amber-700 text-white text-[11px] uppercase tracking-widest font-semibold hover:bg-amber-800 disabled:opacity-50 transition-colors"
                   data-testid="enq-portal-nudge-btn">
            {busy ? "Sending…" : "Send nudge"}
          </button>
          {err && data && (
            <div className="mt-2 text-[11px] text-rose-700" data-testid="enq-portal-inline-err">{err}</div>
          )}
        </section>

        {/* Timeline */}
        {(data.activity || []).length > 0 && (
          <section className="border border-border bg-white p-5" data-testid="enq-portal-timeline">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
              Timeline
            </div>
            <ol className="space-y-2 max-h-96 overflow-y-auto">
              {[...data.activity].reverse().map((a, i) => (
                <li key={i} className="flex gap-3 text-xs border-b border-border/50 pb-2 last:border-0">
                  <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap w-32">
                    {fmt(a.at)}
                  </span>
                  <span className="flex-1">
                    <span className="text-[10px] uppercase tracking-widest text-amber-700/80 font-semibold mr-2">
                      {(a.kind || "").replace(/_/g, " ")}
                    </span>
                    {a.note && <span>{a.note}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <div className="text-center text-[10px] text-muted-foreground mt-8">
          Keep this link — it's the fastest way to check on your enquiry.
        </div>
      </div>
    </main>
  );
}
